import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BTN_LG } from '../constants/buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiGet, apiPost } from '../utils/api';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => { document.documentElement.classList.add('dark'); }, []);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirmPassword;

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    apiGet(`/api/auth/validate-token?token=${encodeURIComponent(token)}&type=password_reset`)
      .then(res => res.json())
      .then(data => {
        setTokenValid(data.valid);
        setValidating(false);
      })
      .catch(() => {
        setValidating(false);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!passwordValid) { setError('Password must be at least 8 characters'); return; }
    if (!passwordsMatch) { setError('Passwords do not match'); return; }

    setSubmitting(true);
    try {
      const res = await apiPost('/api/auth/reset-password', { token, password });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reset failed');
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner message="Validating reset link..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            CREW FORGE
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Set a new password
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          {!token || !tokenValid ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Invalid or expired link</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This password reset link is no longer valid. Please request a new one.
              </p>
              <Link to="/forgot-password" className={`${BTN_LG.primary} inline-flex justify-center`}>
                Request new link
              </Link>
            </div>
          ) : success ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Password reset</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your password has been reset. You can now sign in with your new password.
              </p>
              <Link to="/login" className={`${BTN_LG.primary} inline-flex justify-center`}>
                Sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className={FIELD_LABEL_CLASS}>
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  autoFocus
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="At least 8 characters"
                />
                {password.length > 0 && (
                  <p className={`mt-1 text-xs ${passwordValid ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {passwordValid ? 'Password length is good' : `${8 - password.length} more character${8 - password.length !== 1 ? 's' : ''} needed`}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className={FIELD_LABEL_CLASS}>
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Confirm your new password"
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">Passwords do not match</p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting || !passwordValid || !passwordsMatch}
                className={`${BTN_LG.primary} w-full justify-center`}
              >
                {submitting ? 'Resetting...' : 'Reset password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
